import { useEffect, useState, type ReactNode } from 'react'
import { ArrowUpRight, Check, Copy } from 'lucide-react'
import { BUI_EASE, ClaudeMark, SlackMark } from '@loop3/ui'
import { LUPE_URL } from '@/lib/api/lupe'

const MCP_URL = `${LUPE_URL}/mcp`
const SLACK_URL = 'https://loop3studio.slack.com/'
const CLAUDE_URL = 'https://claude.ai/new'

const card =
  'flex h-full flex-col gap-3 rounded-xl border border-border bg-card/60 p-4 text-left transition-colors'
const linkCard = `${card} group hover:border-[var(--l3-violet)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--l3-violet)]`

/** The other doors to Lupe: Claude (on by default at Loop3), Slack, and any MCP client. */
export function LupeElsewhere() {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(t)
  }, [copied])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL)
      setCopied(true)
    } catch {
      // The URL stays visible and selectable when the clipboard is blocked.
    }
  }

  return (
    <section
      aria-labelledby="lupe-elsewhere"
      className="bui-motion w-full pt-10 pb-16"
      style={{ animation: `bui-fade-up 420ms ${BUI_EASE} 480ms both` }}
    >
      <h2
        id="lupe-elsewhere"
        className="mb-3 text-center text-xs font-medium tracking-wide text-muted-foreground uppercase"
      >
        Also ask Lupe from
      </h2>
      <ul className="grid gap-3 sm:grid-cols-3">
        <li>
          <a
            href={CLAUDE_URL}
            target="_blank"
            rel="noreferrer"
            className={linkCard}
          >
            <Header icon={<ClaudeMark className="size-5" />} name="Claude" />
            <span className="w-fit rounded-full bg-[var(--l3-violet-soft)] px-2 py-0.5 text-xs font-medium text-[var(--l3-violet)]">
              On by default at Loop3
            </span>
            <p className="text-sm text-muted-foreground">
              The Lupe connector is already on in the Loop3 Claude workspace.
              Just ask in any chat.
            </p>
          </a>
        </li>
        <li>
          <a
            href={SLACK_URL}
            target="_blank"
            rel="noreferrer"
            className={linkCard}
          >
            <Header icon={<SlackMark className="size-5" />} name="Slack" />
            <span className="w-fit rounded-lg rounded-bl-sm bg-muted px-2.5 py-1 text-xs">
              <span className="font-medium text-[var(--l3-violet)]">@Lupe</span>{' '}
              who knows MCP gateways?
            </span>
            <p className="text-sm text-muted-foreground">
              DM Lupe or mention @Lupe in any channel of the Loop3 workspace.
            </p>
          </a>
        </li>
        <li className={card}>
          <Header
            icon={
              <span className="grid size-5 place-items-center rounded bg-foreground font-mono text-[9px] font-bold text-background">
                MCP
              </span>
            }
            name="Any AI client"
          />
          <div className="flex items-center gap-1 rounded-lg border border-border bg-background py-0.5 pr-0.5 pl-2.5">
            <code className="min-w-0 flex-1 truncate text-xs select-all">
              {MCP_URL.replace(/^https?:\/\//, '')}
            </code>
            <button
              type="button"
              onClick={copy}
              aria-label={copied ? 'Copied' : 'Copy MCP URL'}
              className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-[var(--l3-violet)]"
            >
              {copied ? (
                <Check className="size-3.5" aria-hidden />
              ) : (
                <Copy className="size-3.5" aria-hidden />
              )}
            </button>
            <span role="status" className="sr-only">
              {copied ? 'Copied' : ''}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Add this URL as a custom connector in ChatGPT, Cursor, or any MCP
            client, then sign in with your Loop3 account.
          </p>
        </li>
      </ul>
    </section>
  )
}

function Header({ icon, name }: { icon: ReactNode; name: string }) {
  return (
    <span className="flex items-center gap-2 text-sm font-medium">
      {icon}
      {name}
      <ArrowUpRight
        aria-hidden
        className="ml-auto size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
      />
    </span>
  )
}
